import type { LocationQuery, RouteRecordNormalized, RouteRecordRaw } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';
import homeRouter from './routes/home.router';
// import creativeInspirationRouter from './routes/creative-inspiration.router';
import myprojectRouter from './routes/myproject.router';
import contentMarketingRouter from './routes/content-marketing.router';
import { useRouterStore } from '@/store/global/router.store';
import { isEmpty, isString, pick } from 'lodash-es';
import { useGlobalGameStore } from '@/store/global/game.store';
import { reportCommonExpose } from '@/report';

export const MENU_LEVEL_SPLIT_FLAG = ' / ';

function attachRouteName(list: Array<RouteRecordRaw>, parentRbac: Array<string> = []): Array<RouteRecordRaw> {
  return list.map((routeCfg: RouteRecordRaw) => {
    const newParentRbac = parentRbac.concat(routeCfg?.meta?.name ?? []);
    const newRouteCfg: any = {
      ...routeCfg,
      meta: {
        ...(routeCfg?.meta || {}),
        parent:
          routeCfg?.meta?.parent ||
          parentRbac.reduce(
            (list, _, index, parentRbac) => list.concat(parentRbac.slice(0, index + 1).join(MENU_LEVEL_SPLIT_FLAG)),
            [] as string[],
          ),
      },
      name: routeCfg?.name ?? newParentRbac.join(MENU_LEVEL_SPLIT_FLAG),
      children: Array.isArray(routeCfg.children)
        ? attachRouteName(routeCfg.children, newParentRbac)
        : undefined,
    };
    return newRouteCfg;
  });
}

function getAllAllowRouter(allRoutes: RouteRecordRaw[] = [], allowRouteNameList?: string[]) {
  return allRoutes.filter(({ name }) => name && isString(name) && allowRouteNameList?.includes(name));
}

function findRouterByName(
  targetRouteName: string | undefined,
  routes: RouteRecordRaw[] = [],
): RouteRecordRaw | undefined {
  return targetRouteName ? routes.find((item) => item.name === targetRouteName) : undefined;
}

function findSiblingRouter(
  targetRoute: RouteRecordNormalized,
  allowRoutes: RouteRecordRaw[] = [],
): RouteRecordRaw | undefined {
  const parentRouter = findParentRouter(targetRoute, allowRoutes);
  if (parentRouter?.children?.length ?? 0 > 0) {
    const childRouter = getAllAllowRouter(
      parentRouter!.children,
      allowRoutes.map((item) => item?.name?.toString()).filter((v) => v) as string[],
    )?.find((router) => router.name !== targetRoute.name);
    if (childRouter) {
      return childRouter;
    }
  }
  return parentRouter ? findParentRouter(parentRouter, allowRoutes) : undefined;
}

function findParentRouter(targetRoute: RouteRecordRaw, allowRoutes: RouteRecordRaw[] = []): RouteRecordRaw | undefined {
  const parentNameList = (Array.isArray(targetRoute?.meta?.parent) ? targetRoute?.meta?.parent : []) ?? [];
  const parentRouterName = parentNameList.at(-1);
  if (!parentRouterName) return undefined;

  return findRouterByName(parentRouterName, allowRoutes);
}

function isRouteIsDir(route: RouteRecordNormalized | RouteRecordRaw | undefined): boolean {
  return !!(route?.meta?.level === 1 || route?.meta?.dir);
}

function getModuleFristRoute(moduleType: string, routeList: RouteRecordRaw[]) {
  return routeList
    .filter((item) => item?.name?.toString()?.startsWith(moduleType) && !item?.meta?.inNotInMenu && !isRouteIsDir(item))
    ?.sort((a, b) => (a?.meta?.index ?? 999) - (b.meta?.index ?? 999))?.[0];
}

// 登录路由
export const notFoundRoute: RouteRecordRaw = {
  path: '/:pathMatch(.*)*',
  name: 'NotFound',
  component: () => import('@/views/404.vue'),
};

// 添加 screen 路由
const screenRoute: RouteRecordRaw = {
  path: '/screen-:game',
  name: 'Screen',
  meta: {
    hide: true,
  },
  redirect: (to) => {
    const game = to.params.game;
    return {
      path: '/myproject/real-time',
      query: {
        isShowMenu: 'false',
        game: game as string
      }
    };
  }
};

const staticRoutesRaw: RouteRecordRaw[] = [homeRouter, myprojectRouter, contentMarketingRouter];
const staticRoutes = attachRouteName(staticRoutesRaw);
const router = createRouter({
  history: createWebHistory(''),
  routes: [...staticRoutes, screenRoute, notFoundRoute],
  scrollBehavior() {
    return { top: 0 };
  },
});

function redirectWithQuery(routerCfg: RouteRecordNormalized | RouteRecordRaw, query: LocationQuery) {
  console.timeLog('router check');
  return {
    ...(routerCfg || {}),
    query,
  };
}

let routeCycleNum = 0;
function isEndRouteCycle() {
  routeCycleNum += 1;
  return routeCycleNum > 10;
}

function endRouteCycle(result: boolean) {
  routeCycleNum = 0;
  return result;
}

export async function refreshRoute(routerGameCode: string) {
  const routerStore = useRouterStore();
  routerStore.setRouterGame(routerGameCode);
  const allRoutes = router.getRoutes();
  routerStore.setAllRoutes(allRoutes);
  routerStore.setRouterDone();
  return allRoutes;
}

router.beforeEach(async (to: any, from) => {
  console.time('router check');
  console.log('to', to);
  // 太湖登录检查 - 未登录时直接跳转到太湖登录
  if (to.meta?.requiresAuth !== false) {
    try {
      const { checkTaihuLogin, redirectToTaihuLogin } = await import('@/utils/taihu-auth');
      if (!checkTaihuLogin()) {
        console.timeEnd('router check');
        // 直接跳转到太湖登录，不需要中间登录页面
        redirectToTaihuLogin();
        return false; // 阻止路由继续
      }
    } catch (error) {
      console.error('认证检查失败:', error);
    }
  }

  // Max Cycle Limit
  if (isEndRouteCycle()) {
    endRouteCycle(false);
    console.error('router max cycle limit', to);
    return notFoundRoute;
  }

  // Loading to Router
  const routerStore = useRouterStore();
  routerStore.setPageLoading();
  const isFirstEnter = from?.path === '/' && from?.name === undefined && from?.matched?.length === 0;

  // 保留参数逻辑
  const retainQuery: string[] = ['cgiMode', 'no_cache', 'update_cache', 'is_use_mock_data'];
  const toHasRetainQuery = retainQuery.some((queryKey: string) => to?.query?.[queryKey]);
  const retainQueryRecord: Record<string, unknown> = toHasRetainQuery ? {} : pick(from?.query, retainQuery);
  if (!isEmpty(retainQueryRecord)) {
    return { ...to, ...{ query: { ...to.query, ...retainQueryRecord } } };
  }

  // Loading to GameList
  const globalGameStore = useGlobalGameStore();
  globalGameStore.setGameCodeLoading();

  // Get Cur-Game
  const gameCodeFrQuery: string | undefined = ((game) => (Array.isArray(game) ? game.at(-1) : game)?.toString())(
    to?.query?.game || from?.query?.game,
  );

  const gameCode = gameCodeFrQuery?.toString() || globalGameStore.storageGameCode || '';
  let routerGameCode: string | undefined = gameCode;

  // Init Router
  switch (true) {
    case routerStore.isRouterLoading:
      console.timeLog('router check');
      return endRouteCycle(false);
    case !routerGameCode || routerStore.checkNowGameIsNeedLoad(routerGameCode): {
      routerStore.setRouterLoading();
      const legalGameCode = await routerStore.initGameRouter({ game: routerGameCode });
      if (!legalGameCode) {
        routerStore.setRouterNeedSelectGame();
        console.timeLog('router check');
        return endRouteCycle(false);
      }

      routerGameCode = legalGameCode;
      const allRoutes = await refreshRoute(routerGameCode);

      const redirectTo: RouteRecordNormalized | undefined =
        to.name === notFoundRoute.name ? allRoutes.find((item) => item.path === to.path) : undefined;

      if (redirectTo) {
        console.timeLog('router check');
        return redirectWithQuery(redirectTo, to?.query);
      }
      break;
    }
    default: {
      routerStore.setRouterGame(routerGameCode);
      const matchRoute: RouteRecordNormalized | undefined = to?.matched?.at(-1);
      const matchRouteName = matchRoute?.name?.toString();
      if (matchRouteName && matchRouteName !== notFoundRoute.name) {
        const allRoutes = router.getRoutes();
        const nowGameAllowRouteNameList = routerStore?.nowGameAllowRoutes?.sort() || [];

        // Dir Router
        if (isRouteIsDir(matchRoute)) {
          const curRouteChildsCfg = allRoutes.find((item) => item.name === matchRouteName)?.children;
          const allowChild = curRouteChildsCfg?.find(
            (item) => isString(item?.name) && nowGameAllowRouteNameList.includes(item?.name),
          );
          const redirectTo =
            allowChild ??
            curRouteChildsCfg?.find((item) => isString(item?.name));
          if (redirectTo) {
            console.timeLog('router check');
            return redirectWithQuery(redirectTo, to?.query);
          }
        }

        // Home Router
        const nowGameAllowRoutes = getAllAllowRouter(allRoutes, nowGameAllowRouteNameList);
        if (homeRouter.name === matchRouteName) {
          const fristBIRoutes = getModuleFristRoute('BI', nowGameAllowRoutes);
          const redirectTo =
            fristBIRoutes || nowGameAllowRoutes?.find((item) => item.name !== matchRouteName && !item.meta?.isInMenu);
          if (redirectTo) {
            return redirectWithQuery(redirectTo, {
              ...(to?.query || {}),
              ...(routerGameCode?.toString()
                ? {
                    game: routerGameCode,
                  }
                : {}),
            });
          }
        }

        // Non-existent Router
        if (matchRouteName && !nowGameAllowRouteNameList.includes(matchRouteName!)) {
          const siblingRoute = findSiblingRouter(matchRoute!, nowGameAllowRoutes);
          if (siblingRoute) {
            console.timeLog('router check');
            return redirectWithQuery(siblingRoute, to?.query);
          }
          const routeObj =
            allRoutes?.reduce(
              (routeObj: Record<string, RouteRecordNormalized>, route) => ({
                ...routeObj,
                [route?.name?.toString() || '']: route,
              }),
              {},
            ) || {};

          const parentRouteNameList = matchRoute?.meta?.parent || [];
          if (!isFirstEnter && parentRouteNameList.length > 0) {
            const compareRouteName =
              parentRouteNameList.length === 1 ? matchRouteName : routeObj[parentRouteNameList.at(1)!].name;
            const level1Route = routeObj[parentRouteNameList.at(0)!];
            const isFristRoute = level1Route.children[0]?.name === compareRouteName;
            const isOneChildren = level1Route.children.length === 1;
            if (isOneChildren) return endRouteCycle(true);
            const level2RouteFirstChild = level1Route.children.at(-1);
            if (isFristRoute && level2RouteFirstChild) return redirectWithQuery(level2RouteFirstChild, to?.query);
          }
          const allowRouteName = nowGameAllowRouteNameList.filter((routeName) => routeObj[routeName])?.[0];
          const firstAllowRoute = routeObj[allowRouteName];
          if (!isFirstEnter && firstAllowRoute) {
            console.timeLog('router check');
            return redirectWithQuery(firstAllowRoute, to?.query);
          }
        }
      }
      break;
    }
  }

  // Pass Game Query
  if (!to?.query?.game && routerGameCode) {
    console.timeLog('router check');
    return { ...to, ...{ query: { ...to.query, game: routerGameCode } } };
  }

  console.timeLog('router check');
  return endRouteCycle(true);
});

router.beforeResolve(() => {
  console.log('before resolve');
  console.timeLog('router check');
});

router.afterEach(async (to) => {
  console.timeEnd('router check');
  const routerStore = useRouterStore();
  const reportId = to?.meta?.reportId;
  const routeName = to.name?.toString() || to?.meta?.name;
  const globalGameStore = useGlobalGameStore();
  if (reportId) {
    reportCommonExpose(reportId, routeName);
  }

  routerStore.setPageLoadEnd();
  routerStore.setFirstRoute(to.matched[0]);
  routerStore.setSecondThreeRouteName(to.matched || []);

  globalGameStore.setGameCode(to?.query?.game as string);
  globalGameStore.setGameCodeDone();
  endRouteCycle(true);
});

router.onError((e) => {
  console.error('error', e);
  const routerStore = useRouterStore();
  routerStore.setPageErr();
});

export default router;
